import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsDownload } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import RadioButton from '#globalComponents/RadioButton/RadioButton';
import RolePriorityPicker from '#globalComponents/RolePriorityPicker/RolePriorityPicker';
import { useError } from '#globalContexts/ErrorContext';
import { CSV_EXPORT_COLUMNS } from '#leadOverviewFeature/constants/csvExportColumns';
import { sanitizeRolePriority } from '#lib/constants/contactRoles';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

import { downloadCSVFile } from '../../api/csvExportApi';
import { useLeadUI } from '../../contexts/LeadUIContext';
import { useActionScope } from '../../hooks/useActionScope';
import { useExportProspectQueryToCSV } from '../../models/prospectQueries/mutations';

import ColumnSelector from './ColumnSelector';

const CSVExportModal = ({ isOpen, onClose, prospectQueryId, prospectQueryName }) => {
	const { hasContactSelection, selectedContacts, leadActionTarget } = useLeadUI();
	const actionScope = useActionScope();
	// Lead selection wins over contact selection, mirroring the bulk-action bars in LeadView
	const { showSuccess, showError } = useError();

	const [selectedColumns, setSelectedColumns] = useState(CSV_EXPORT_COLUMNS);
	const [oneContactPerCompany, setOneContactPerCompany] = useState(false);
	const [reachableBy, setReachableBy] = useState('all');

	// Last-used priority is persisted in the preferences store (shared with add-to-list).
	const storedRolePriority = usePreferencesStore((s) => s.contactRolePriority);
	const setContactRolePriority = usePreferencesStore((s) => s.setContactRolePriority);
	const rolePriority = useMemo(() => sanitizeRolePriority(storedRolePriority), [storedRolePriority]);

	// Check if contact columns are selected
	const hasContactColumns = useMemo(() => {
		const contactCols = ['contactName', 'contactRole', 'contactEmail', 'contactPhone'];
		return selectedColumns.some((col) => contactCols.includes(col));
	}, [selectedColumns]);

	// CSV Export mutation
	const exportMutation = useExportProspectQueryToCSV({
		onSuccess: ({ blob, filename }) => {
			// Download the file
			downloadCSVFile(blob, filename);

			// Show success modal
			showSuccess({
				title: 'CSV-Export erfolgreich',
				message: 'Ihre CSV-Daten wurden erfolgreich exportiert',
			});

			// Close modal after successful export
			onClose();
		},
	});

	const handleExport = () => {
		if (selectedColumns.length === 0) {
			showError({
				title: 'Keine Spalten ausgewählt',
				message: 'Bitte wählen Sie mindestens eine Spalte aus.',
			});
			return;
		}

		// A contact selection exports exactly those contacts — no company scoping.
		const options = {
			columns: selectedColumns,
			...(hasContactSelection ? { contactIds: selectedContacts } : actionScope.scope),
		};

		// One recipient per company, chosen by the user's role priority
		if (hasContactColumns && !hasContactSelection && oneContactPerCompany) {
			options.rolePriority = rolePriority;
		}

		if (reachableBy !== 'all') {
			options.reachableBy = reachableBy;
		}

		exportMutation.mutate({ prospectQueryId, options });
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="l">
			<Panel.Header onClose={onClose}>CSV Export Optionen</Panel.Header>
			<Panel.Body>
				<div className="space-y-6">
					<div className="bg-bgBase-200 p-4 rounded-lg">
						<p className="text-sm text-text">Export für Suchanfrage:</p>
						<p className="text-lg font-semibold text-text">{prospectQueryName}</p>
						<p className="text-sm text-primary-600 mt-1 font-medium">
							Exportiert werden{' '}
							{hasContactSelection ? `${selectedContacts.length} ausgewählte Kontakte` : leadActionTarget}
						</p>
					</div>

					<div className="border-t pt-4">
						<ColumnSelector selectedColumns={selectedColumns} onChange={setSelectedColumns} />
					</div>

					{/* One recipient per company (pointless when specific contacts are selected) */}
					{hasContactColumns && !hasContactSelection && (
						<div className="bg-primary-100 border border-primary-200 rounded-lg p-4">
							<Checkbox
								id="csv-export-one-contact-per-company"
								label="Exportiere nur einen Kontakt pro Firma (nach Rollen-Priorität)"
								checked={oneContactPerCompany}
								onChange={() => setOneContactPerCompany(!oneContactPerCompany)}
							/>
							{oneContactPerCompany && (
								<div className="mt-3 rounded-lg border border-primary-200 bg-white p-3">
									<RolePriorityPicker value={rolePriority} onChange={setContactRolePriority} />
									<p className="mt-2 text-xs text-bgBase-800">
										Pro Firma wird der erste Ansprechpartner gemäß dieser Reihenfolge exportiert.
										Firmen ohne passenden Eintrag werden ausgelassen; „Firmenkontakt“ exportiert die
										Firma selbst (ohne Ansprechpartner).
									</p>
								</div>
							)}
						</div>
					)}

					<div className="border-t pt-4">
						<label className="block text-md font-medium text-text mb-3">Empfänger einschränken?</label>
						<div className="space-y-1 border border-bgBase-400 rounded-lg p-3">
							<RadioButton
								name="csvReachableBy"
								value="all"
								checked={reachableBy === 'all'}
								onChange={setReachableBy}
								label="Alle exportieren"
								size="md"
							/>
							<RadioButton
								name="csvReachableBy"
								value="email"
								checked={reachableBy === 'email'}
								onChange={setReachableBy}
								label="Nur Empfänger mit E-Mail-Adresse (für E-Mail-Kampagnen)"
								size="md"
							/>
							<RadioButton
								name="csvReachableBy"
								value="post"
								checked={reachableBy === 'post'}
								onChange={setReachableBy}
								label="Nur Firmen mit Postadresse (für Post-Mailings)"
								size="md"
							/>
						</div>
					</div>
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={onClose} disabled={exportMutation.isPending}>
					Abbrechen
				</Button>
				<Button variant="solid" onClick={handleExport} disabled={exportMutation.isPending}>
					{exportMutation.isPending ? (
						<>
							<div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
							Exportiere...
						</>
					) : (
						<>
							<BsDownload />
							CSV Exportieren
						</>
					)}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

CSVExportModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	prospectQueryId: PropTypes.string.isRequired,
	prospectQueryName: PropTypes.string.isRequired,
};

export default CSVExportModal;
